// @ts-check

export * from './draft';
export * from './edit';
export * from './favourites';

/** @typedef {import('../state').ExportTemplateState} ExportTemplateState */
/** @typedef {import('../state').SheetMetadata} SheetMetadata */
/** @typedef {import('../state').DropdownTemplate} DropdownTemplate */
/** @typedef {import('../state').SearchConfig} SearchConfig */

/**
 * Sheet metadata has been received from the server.
 * @param {ExportTemplateState} state
 * @param {SheetMetadata[]} payload
 * @returns {ExportTemplateState}
 */
export const sheetMetadataReceived = (state, payload) => ({
  ...state,
  sheetMetadata: payload.map(smd => ({
    ...smd,
    columns: smd.columns.map(cmd => ({
      ...cmd,
      name: cmd.name.replace('{0}', '').trim(),
    })),
  })),
});

/**
 * Minimal templates have been received from the server.
 *
 * Selection is sticky: the favourite is auto-selected only on the first load.
 * On later refetches (e.g. after a save) the user's selection is preserved if it
 * still exists, otherwise it falls back to the favourite, else the default.
 * @param {ExportTemplateState} state
 * @param {{ data: DropdownTemplate[], favouriteId?: number }} payload
 * @returns {ExportTemplateState}
 */
export const minimalTemplatesReceived = (state, payload) => {
  const firstLoad = !state.templatesLoaded;
  const selectionValid =
    state.selectedTemplateId === 'all' ||
    payload.data.some(t => t.id === state.selectedTemplateId);

  return {
    ...state,
    dropdownTemplates: payload.data,
    favouriteTemplateId: payload.favouriteId ?? null,
    templatesLoaded: true,
    selectedTemplateId:
      firstLoad || !selectionValid
        ? payload.favouriteId ?? 'all'
        : state.selectedTemplateId,
  };
};

/**
 * A server mutation occurred. Bumps the counter to trigger a list refresh in
 * hooks; selection is left untouched and reconciled by minimalTemplatesReceived.
 * @param {ExportTemplateState} state
 * @returns {ExportTemplateState}
 */
export const serverMutationOccurred = state => ({
  ...state,
  searchConfig: {
    ...state.searchConfig,
    mutationCount: state.searchConfig.mutationCount + 1,
  },
});

/**
 * User changed the search configuration on the Export Template page.
 * @param {ExportTemplateState} state
 * @param {Partial<SearchConfig>} payload
 * @returns {ExportTemplateState}
 */
export const searchConfigurationChanged = (state, payload) => ({
  ...state,
  searchConfig: {
    ...state.searchConfig,
    ...payload,
  },
});

/**
 * User selected a different template from the export dropdown.
 * @param {ExportTemplateState} state
 * @param {number} payload
 * @returns {ExportTemplateState}
 */
export const templateSelectionChanged = (state, payload) => ({
  ...state,
  selectedTemplateId: payload,
});

/**
 * Adds a newly created template to the export summary dropdown immediately,
 * before the minimal-list refetch completes.
 * @param {ExportTemplateState} state
 * @param {DropdownTemplate} payload
 * @returns {ExportTemplateState}
 */
export const dropdownTemplateAdded = (state, payload) => ({
  ...state,
  dropdownTemplates: [...state.dropdownTemplates, payload],
});
